// /admin/transactions — chronological cross-client payments ledger.
//
// Sister surface to /admin/finance (which is invoice-centric); this is the
// movement-of-funds view: every Payment row across every client, filterable
// by method / status / and time window. Click a row to drill into the
// originating invoice (where the operator can mark-paid / void / refund).

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiGet, ApiError } from '../../lib/api';

type Method = 'card' | 'wire';
type PaymentStatus = 'pending' | 'succeeded' | 'failed' | 'refunded';
type MethodFilter = 'all' | Method;
type StatusFilter = 'all' | PaymentStatus;
type WindowFilter = '7d' | '30d' | '90d' | 'all';

interface PaymentRow {
  id: string;
  invoice_id: string;
  invoice_status: string | null;
  payment_reference: string | null;
  client_id: string;
  client_name: string | null;
  method: Method;
  card_network: string | null;
  card_last4: string | null;
  amount_minor: number;
  currency: string;
  status: PaymentStatus;
  external_ref: string | null;
  revolut_order_id: string | null;
  parent_payment_id: string | null;
  refund_amount_minor: number | null;
  refund_reason: string | null;
  failure_code: string | null;
  failure_message: string | null;
  created_at: string;
}

const STATUS_TONE: Record<PaymentStatus, string> = {
  pending: 'amber',
  succeeded: 'green',
  failed: 'red',
  refunded: 'red',
};

const STATUS_LABEL: Record<PaymentStatus, string> = {
  pending: 'Pending',
  succeeded: 'Succeeded',
  failed: 'Failed',
  refunded: 'Refunded',
};

function formatMoney(amount_minor: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency, maximumFractionDigits: 2 })
      .format(amount_minor / 100);
  } catch {
    return `${currency} ${(amount_minor / 100).toLocaleString()}`;
  }
}

function windowToSinceIso(w: WindowFilter): string | null {
  if (w === 'all') return null;
  const days = w === '7d' ? 7 : w === '30d' ? 30 : 90;
  return new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
}

export function AdminTransactions() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<PaymentRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [methodFilter, setMethodFilter] = useState<MethodFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [windowFilter, setWindowFilter] = useState<WindowFilter>('30d');

  // Fetch on window change. Method + status are applied client-side so the
  // toggles feel snappy without a round-trip.
  useEffect(() => {
    const since = windowToSinceIso(windowFilter);
    const params = new URLSearchParams();
    if (since) params.set('since', since);
    params.set('limit', '200');
    apiGet<{ payments: PaymentRow[] }>(`/api/payments?${params.toString()}`)
      .then((r) => setRows(r.payments))
      .catch((err: Error) => setError(err instanceof ApiError ? err.serverMessage : err.message));
  }, [windowFilter]);

  const list = useMemo(
    () => (rows ?? []).filter((r) => {
      if (methodFilter !== 'all' && r.method !== methodFilter) return false;
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      return true;
    }),
    [rows, methodFilter, statusFilter],
  );

  // Top-of-page totals — sum of succeeded payments by currency in the visible
  // window (filtered by all three controls), plus a count of pending/failed.
  const totals = useMemo(() => {
    const byCurrency = new Map<string, number>();
    let pending = 0;
    let failed = 0;
    let refunded = 0;
    for (const r of list) {
      if (r.status === 'succeeded') {
        byCurrency.set(r.currency, (byCurrency.get(r.currency) ?? 0) + r.amount_minor);
      } else if (r.status === 'pending') pending += 1;
      else if (r.status === 'failed') failed += 1;
      else if (r.status === 'refunded') refunded += 1;
    }
    return { byCurrency, pending, failed, refunded };
  }, [list]);

  return (
    <>
      <div>
        <h1 className="portal-h1">Transactions</h1>
        <p className="portal-sub">
          Chronological ledger of every payment attempt — card, wire, and refund — across all clients.
          For invoice-level workflow (issue / mark-paid / refund) use{' '}
          <Link to="/admin/finance" style={{ borderBottom: '1px solid var(--rule)' }}>Finance</Link>.
        </p>
      </div>

      <div className="portal-cards" style={{ marginTop: 16 }}>
        <div className="card">
          <div className="lbl">Cleared in window</div>
          <div style={{ fontSize: 14, marginTop: 4 }}>
            {Array.from(totals.byCurrency.entries()).map(([ccy, amt]) => (
              <div key={ccy} style={{ display: 'flex', justifyContent: 'space-between', fontVariantNumeric: 'tabular-nums' }}>
                <span style={{ color: 'var(--fg-mute)' }}>{ccy}</span>
                <strong>{formatMoney(amt, ccy)}</strong>
              </div>
            ))}
            {totals.byCurrency.size === 0 && (
              <span style={{ color: 'var(--fg-mute)', fontSize: 12 }}>Nothing cleared</span>
            )}
          </div>
        </div>
        <div className="card">
          <div className="lbl">Pending</div>
          <div className="val">{totals.pending}</div>
          <div className="delta">Awaiting webhook / wire</div>
        </div>
        <div className="card">
          <div className="lbl">Failed</div>
          <div className="val">{totals.failed}</div>
          <div className="delta">Card declines</div>
        </div>
        <div className="card">
          <div className="lbl">Refunded</div>
          <div className="val">{totals.refunded}</div>
          <div className="delta">Returned to clients</div>
        </div>
      </div>

      {/* Filter row — window first (round-trip), then method + status */}
      <div className="tabs" style={{ marginTop: 24 }}>
        <span style={{ color: 'var(--fg-mute)', fontSize: 12, marginRight: 8 }}>Window:</span>
        {(['7d', '30d', '90d', 'all'] as WindowFilter[]).map((t) => (
          <button key={t} className={windowFilter === t ? 'on' : ''} onClick={() => setWindowFilter(t)}>
            {t === 'all' ? 'All' : t}
          </button>
        ))}
      </div>
      <div className="tabs" style={{ marginTop: 8 }}>
        <span style={{ color: 'var(--fg-mute)', fontSize: 12, marginRight: 8 }}>Method:</span>
        {(['all', 'card', 'wire'] as MethodFilter[]).map((t) => (
          <button key={t} className={methodFilter === t ? 'on' : ''} onClick={() => setMethodFilter(t)}>
            {t === 'all' ? 'All' : t === 'card' ? 'Card' : 'Wire'}
          </button>
        ))}
      </div>
      <div className="tabs" style={{ marginTop: 8 }}>
        <span style={{ color: 'var(--fg-mute)', fontSize: 12, marginRight: 8 }}>Status:</span>
        {(['all', 'pending', 'succeeded', 'failed', 'refunded'] as StatusFilter[]).map((t) => (
          <button key={t} className={statusFilter === t ? 'on' : ''} onClick={() => setStatusFilter(t)}>
            {t === 'all' ? 'All' : STATUS_LABEL[t]}
          </button>
        ))}
      </div>

      {error && (
        <div className="card" style={{ marginTop: 16, borderColor: 'var(--red)', color: 'var(--red)' }}>
          {error}
        </div>
      )}

      <div className="card flush" style={{ marginTop: 16 }}>
        <table className="tbl">
          <thead>
            <tr>
              <th>When</th>
              <th>Reference</th>
              <th>Client</th>
              <th>Method</th>
              <th>Invoice</th>
              <th className="amt">Amount</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {rows === null && (
              <tr><td colSpan={7} style={{ color: 'var(--fg-mute)', fontSize: 13 }}>Loading…</td></tr>
            )}
            {rows !== null && list.length === 0 && (
              <tr>
                <td colSpan={7} style={{ color: 'var(--fg-mute)', fontSize: 13, textAlign: 'center', padding: 32 }}>
                  No transactions match these filters.
                </td>
              </tr>
            )}
            {list.map((p) => (
              <tr
                key={p.id}
                className="row-clickable"
                tabIndex={0}
                onClick={() => navigate(`/admin/invoices/${p.invoice_id}`)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate(`/admin/invoices/${p.invoice_id}`);
                  }
                }}
              >
                <td style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--fg-mute)', whiteSpace: 'nowrap' }}>
                  {new Date(p.created_at).toISOString().slice(0, 16).replace('T', ' ')}
                </td>
                <td>
                  <span className="rid">{p.id}</span>
                  {p.parent_payment_id && (
                    <div style={{ fontSize: 11, color: 'var(--fg-mute)', fontFamily: 'var(--mono)' }}>
                      ↳ refund of {p.parent_payment_id}
                    </div>
                  )}
                </td>
                <td style={{ fontSize: 13 }}>
                  <Link to={`/admin/clients/${p.client_id}`} onClick={(e) => e.stopPropagation()}>
                    {p.client_name ?? p.client_id}
                  </Link>
                </td>
                <td style={{ fontSize: 13, color: 'var(--fg-soft)' }}>
                  {p.method === 'card' ? (
                    <>
                      Card
                      {p.card_network && (
                        <span style={{ fontSize: 11, color: 'var(--fg-mute)', marginLeft: 6 }}>
                          {p.card_network}{p.card_last4 ? ` ····${p.card_last4}` : ''}
                        </span>
                      )}
                    </>
                  ) : (
                    <>
                      Wire
                      {p.external_ref && (
                        <span style={{ fontSize: 11, color: 'var(--fg-mute)', marginLeft: 6, fontFamily: 'var(--mono)' }}>
                          {p.external_ref}
                        </span>
                      )}
                    </>
                  )}
                </td>
                <td style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--fg-soft)' }}>
                  {p.invoice_id}
                </td>
                <td className="amt" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {p.status === 'refunded' && p.refund_amount_minor != null
                    ? `(${formatMoney(p.refund_amount_minor, p.currency)})`
                    : formatMoney(p.amount_minor, p.currency)}
                </td>
                <td>
                  <span className={`chip ${STATUS_TONE[p.status]}`}>
                    <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'currentColor' }} />
                    {STATUS_LABEL[p.status]}
                  </span>
                  {p.status === 'failed' && p.failure_code && (
                    <div style={{ fontSize: 11, color: 'var(--fg-mute)', fontFamily: 'var(--mono)', marginTop: 2 }}>
                      {p.failure_code}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
